import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { IDLE_WARNING_MS } from "auth/constants";
import { authStore } from "auth/authStore";

function scheduleFromActivity(lastActivityAt, onIdleWarning) {
  const elapsed = Date.now() - lastActivityAt;
  const warningDelay = Math.max(0, IDLE_WARNING_MS - elapsed);

  return setTimeout(() => {
    onIdleWarning?.();
  }, warningDelay);
}

/**
 * Opens the inactivity dialog after IDLE_WARNING_MS.
 * Auto-logout is owned by SessionExpiredDialog's countdown.
 */
export default function useSessionIdle({
  enabled,
  onIdleWarning,
}) {
  const warningTimerRef = useRef(null);
  const onIdleWarningRef = useRef(onIdleWarning);

  const { lastActivityAt } = useSyncExternalStore(
    authStore.subscribe,
    authStore.getState,
    authStore.getState,
  );

  useEffect(() => {
    onIdleWarningRef.current = onIdleWarning;
  }, [onIdleWarning]);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const resetIdle = useCallback(() => {
    clearTimers();

    if (!enabled || !lastActivityAt) {
      return;
    }

    warningTimerRef.current = scheduleFromActivity(
      lastActivityAt,
      () => onIdleWarningRef.current?.(),
    );
  }, [clearTimers, enabled, lastActivityAt]);

  useEffect(() => {
    resetIdle();
    return clearTimers;
  }, [resetIdle, clearTimers]);

  return { resetIdle };
}
